import React, { useState, useEffect, useLayoutEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { useMsal } from '@azure/msal-react';
import { loginRequest } from '../auth/authConfig';
import store from '../store/store';
import type { RootState } from '../store/store';
import DashboardLayout from './DashboardLayout';
import { uploadFileToSharePointWithFile, fetchUploadedFiles, deleteFileFromSharePoint, type FileDisplayData } from '../Services/BackingCodingFileUpload';
import { collectBackingCodingRecord, savesectionApicall, fetchCycleData, type CycleData } from '../Services/BackingCodingRecord';
import { addOfflineData } from '../store/BackingCodingSlice';
import { storeFileOffline, getFileDisplayData } from '../Services/BackingCodingOfflineFileUpload';
import { showOfflineSaveAlertForCategory } from '../utils/offlineAlerts';

const BACKING_CODING_INITIAL_KEY = 'backingCodingInitialData';

interface BackingCodingInitialData {
  productName: string;
  executiveName: string;
}

function getBackingCodingInitial(): BackingCodingInitialData | null {
  try {
    const s = localStorage.getItem(BACKING_CODING_INITIAL_KEY);
    return s ? (JSON.parse(s) as BackingCodingInitialData) : null;
  } catch {
    return null;
  }
}

function saveBackingCodingInitial(data: BackingCodingInitialData): void {
  try {
    localStorage.setItem(BACKING_CODING_INITIAL_KEY, JSON.stringify(data));
  } catch (e) {
    console.warn('Could not save Backing Coding initial data', e);
  }
}

const MAX_CYCLES = 100;

interface CycleProductData {
  cycleNum: string;
  product: string;
  executiveName: string;
  sku: string;
  machineProof: string;
  remarks: string;
}

const BackingCoding: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { accounts, instance } = useMsal();
  const { selectedCycle, plantTourId } = useSelector((state: RootState) => state.planTour ?? {});
  const user = useSelector((state: RootState) => state.user?.user);
  const isOfflineStarted = useSelector((state: RootState) => state.appState?.isOfflineStarted ?? false);
  const reduxFetchedCycles = useSelector((state: RootState) => state.backingCoding?.fetchedCycles ?? []);

  const now = new Date();
  const formattedDate = `${String(now.getDate()).padStart(2, '0')}/${String(now.getMonth() + 1).padStart(2, '0')}/${now.getFullYear()}`;

  const idx = (cycle: number) => Math.max(0, Math.min(cycle - 1, MAX_CYCLES - 1));

  const emptyStartForm = { productName: '', executiveName: '' };
  const emptySaveForm = { sku: '', machineProof: '', remarks: '' };

  const offlineInitial = useMemo(() => {
    try {
      const s = store.getState();
      if (!s?.appState?.isOfflineStarted) return null;
      const fetched = s?.backingCoding?.fetchedCycles ?? [];
      if (!Array.isArray(fetched) || fetched.length === 0) return null;
      const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      const idxFn = (c: number) => Math.max(0, Math.min(c - 1, MAX_CYCLES - 1));
      fetched.forEach((cycleData: any) => {
        let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idxFn(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            sku: cycleData.sku || '',
            machineProof: cycleData.machineProof || '',
            remarks: cycleData.remarks || '',
          });
        }
      });
      const completedCycles = Array(MAX_CYCLES).fill(false);
      byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
      const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
      return { savedProducts: byCycle, cycleCompleted: completedCycles, currentCycle: maxCycleFromData + 1 };
    } catch {
      return null;
    }
  }, []);

  const [cycleCompleted, setCycleCompleted] = useState<boolean[]>(() => offlineInitial?.cycleCompleted ?? Array(MAX_CYCLES).fill(false));
  const [isSessionStarted, setIsSessionStarted] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [cycleExpanded, setCycleExpanded] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [savedProducts, setSavedProducts] = useState<CycleProductData[][]>(() => offlineInitial?.savedProducts ?? Array(MAX_CYCLES).fill(null).map(() => []));
  const [showStartFormForNewProduct, setShowStartFormForNewProduct] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [formDataByCycle, setFormDataByCycle] = useState<typeof emptyStartForm[]>(() => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptyStartForm })));
  const [saveFormDataByCycle, setSaveFormDataByCycle] = useState<typeof emptySaveForm[]>(() => Array(MAX_CYCLES).fill(null).map(() => ({ ...emptySaveForm })));
  const [selectedFile, setSelectedFile] = useState<(File | null)[]>(() => Array(MAX_CYCLES).fill(null));
  const [isUploading, setIsUploading] = useState<boolean[]>(() => Array(MAX_CYCLES).fill(false));
  const [uploadedFiles, setUploadedFiles] = useState<FileDisplayData[][]>(() => Array(MAX_CYCLES).fill(null).map(() => []));
  const [currentCycle, setCurrentCycle] = useState(() => offlineInitial?.currentCycle ?? 1);
  const [expandedCompletedCycles, setExpandedCompletedCycles] = useState<Set<number>>(new Set());
  const systemFileInputRefs = useRef<(HTMLInputElement | null)[]>(Array(MAX_CYCLES).fill(null));
  const cameraFileInputRefs = useRef<(HTMLInputElement | null)[]>(Array(MAX_CYCLES).fill(null));
  const [cameraCaptureCycle, setCameraCaptureCycle] = useState<number | null>(null);
  const [cameraError, setCameraError] = useState<string | null>(null);
  const [isCameraReady, setIsCameraReady] = useState(false);
  const cameraVideoRef = useRef<HTMLVideoElement | null>(null);
  const cameraCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const cameraStreamRef = useRef<MediaStream | null>(null);

  const setCycleCompletedAt = (cycle: number, value: boolean) => setCycleCompleted(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setIsSessionStartedAt = (cycle: number, value: boolean) => setIsSessionStarted(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setCycleExpandedAt = (cycle: number, value: boolean) => setCycleExpanded(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setSavedProductsAt = (cycle: number, updater: (prev: CycleProductData[]) => CycleProductData[]) => setSavedProducts(prev => { const n = prev.map(a => [...a]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });
  const setShowStartFormForNewProductAt = (cycle: number, value: boolean) => setShowStartFormForNewProduct(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setFormDataByCycleAt = (cycle: number, value: typeof emptyStartForm) => setFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[idx(cycle)] = value; return n; });
  const setSaveFormDataByCycleAt = (cycle: number, value: typeof emptySaveForm) => setSaveFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[idx(cycle)] = value; return n; });
  const setSelectedFileAt = (cycle: number, value: File | null) => setSelectedFile(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setIsUploadingAt = (cycle: number, value: boolean) => setIsUploading(prev => { const n = [...prev]; n[idx(cycle)] = value; return n; });
  const setUploadedFilesAt = (cycle: number, updater: (prev: FileDisplayData[]) => FileDisplayData[]) => setUploadedFiles(prev => { const n = prev.map(a => [...a]); n[idx(cycle)] = updater(n[idx(cycle)]); return n; });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, cycle: number = 1, isSaveForm: boolean = false) => {
    const { name, value } = e.target;
    const i = idx(cycle);
    if (isSaveForm) {
      setSaveFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[i] = { ...n[i], [name]: value }; return n; });
    } else {
      setFormDataByCycle(prev => { const n = prev.map(x => ({ ...x })); n[i] = { ...n[i], [name]: value }; return n; });
    }
  };

  const handleStartSession = (cycle: number = 1) => {
    const data = formDataByCycle[idx(cycle)];
    saveBackingCodingInitial({ productName: data.productName || '', executiveName: data.executiveName || '' });
    setShowStartFormForNewProductAt(cycle, false);
    setIsSessionStartedAt(cycle, true);
    setUploadedFilesAt(cycle, () => []);
    setSelectedFileAt(cycle, null);
  };

  const handleCancelSession = (cycle: number = 1) => setIsSessionStartedAt(cycle, false);

  const handleSaveSession = async (cycle: number = 1) => {
    if (!plantTourId) {
      alert('Plant Tour ID is missing. Please start a plant tour first.');
      return;
    }
    const i = idx(cycle);
    const saveData = saveFormDataByCycle[i];
    const startData = formDataByCycle[i];
    const userName = user?.Name || accounts[0]?.name || null;
    const product = (startData.productName || '').trim() || 'N/A';
    const executiveName = (startData.executiveName || '').trim() || 'N/A';
    const skuVal = (saveData.sku || '').trim() || null;
    const proofVal = (saveData.machineProof || '').trim() || null;
    const remarksVal = (saveData.remarks || '').trim() || null;

    const cycleFiles = (uploadedFiles[i] || []).filter(f => (Number(f.cycleNum) || 1) === cycle);
    const productKey = product.toLowerCase();
    const filesForProduct = cycleFiles.filter(f => ((f.productName || '').trim().toLowerCase()) === productKey);
    const attachmentNames = filesForProduct.length ? filesForProduct.map(f => f.fileName).join(', ') : null;

    try {
      if (isOfflineStarted) {
        const record = collectBackingCodingRecord(cycle, product, executiveName, skuVal, proofVal, remarksVal, plantTourId, userName, attachmentNames);
        dispatch(addOfflineData({
          cycleNo: cycle,
          records: [{
            cycleNum: String(cycle),
            product,
            executiveName,
            sku: saveData.sku || '',
            machineProof: saveData.machineProof || '',
            remarks: saveData.remarks || '',
          }],
          timestamp: Date.now(),
        }));
        setSavedProductsAt(cycle, prev => [...prev, {
          cycleNum: String(cycle),
          product,
          executiveName,
          sku: saveData.sku || '',
          machineProof: saveData.machineProof || '',
          remarks: saveData.remarks || '',
        }]);
        setIsSessionStartedAt(cycle, false);
        setShowStartFormForNewProductAt(cycle, false);
        try {
          const key = `backing-coding-cycle-${cycle}-data`;
          const existing = localStorage.getItem(key);
          const existingArr = existing ? (JSON.parse(existing) as any[]) : [];
          const merged = Array.isArray(existingArr) ? [...existingArr, record] : [record];
          localStorage.setItem(key, JSON.stringify(merged));
        } catch (_) {}
        showOfflineSaveAlertForCategory(cycle, 'Backing Coding');
      } else {
        const record = collectBackingCodingRecord(cycle, product, executiveName, skuVal, proofVal, remarksVal, plantTourId, userName, attachmentNames);
        const result = await savesectionApicall([record]);
        if (result.success) {
          setSavedProductsAt(cycle, prev => [...prev, {
            cycleNum: String(cycle),
            product,
            executiveName,
            sku: saveData.sku || '',
            machineProof: saveData.machineProof || '',
            remarks: saveData.remarks || '',
          }]);
          setIsSessionStartedAt(cycle, false);
          setShowStartFormForNewProductAt(cycle, false);
        } else {
          alert(`Failed to save Cycle ${cycle}: ${result.message}`);
        }
      }
    } catch (error) {
      console.error('Error saving session:', error);
      alert(`Error: ${error instanceof Error ? error.message : 'Failed to save session.'}`);
    }
  };

  const handleBack = () => {
    if (window.history.length > 1) navigate(-1);
    else navigate('/home');
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>, cycle: number) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFileAt(cycle, file);
  };
  const resetAttachmentInputs = (cycle: number) => {
    const i = idx(cycle);
    const systemInput = systemFileInputRefs.current[i];
    const cameraInput = cameraFileInputRefs.current[i];
    if (systemInput) systemInput.value = '';
    if (cameraInput) cameraInput.value = '';
  };
  const triggerSystemImagePicker = (cycle: number) => {
    systemFileInputRefs.current[idx(cycle)]?.click();
  };
  const stopCameraStream = () => {
    if (!cameraStreamRef.current) return;
    cameraStreamRef.current.getTracks().forEach(track => track.stop());
    cameraStreamRef.current = null;
  };
  const closeCameraCapture = () => {
    stopCameraStream();
    setCameraCaptureCycle(null);
    setCameraError(null);
    setIsCameraReady(false);
  };
  const openCameraCapture = async (cycle: number) => {
    setCameraError(null);
    setIsCameraReady(false);
    if (!navigator.mediaDevices?.getUserMedia) {
      cameraFileInputRefs.current[idx(cycle)]?.click();
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: 'environment' } },
        audio: false,
      });
      cameraStreamRef.current = stream;
      setCameraCaptureCycle(cycle);
    } catch (error) {
      console.error('Camera access error:', error);
      cameraFileInputRefs.current[idx(cycle)]?.click();
      setCameraError('Unable to open live camera. Using device camera picker instead.');
    }
  };
  const handleCapturePhoto = async () => {
    if (cameraCaptureCycle == null) return;
    const video = cameraVideoRef.current;
    const canvas = cameraCanvasRef.current;
    if (!video || !canvas) return;
    const width = video.videoWidth || 1280;
    const height = video.videoHeight || 720;
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.drawImage(video, 0, 0, width, height);
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob(resolve, 'image/jpeg', 0.92);
    });
    if (!blob) {
      setCameraError('Failed to capture image from camera.');
      return;
    }
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
    const capturedFile = new File([blob], `captured_${timestamp}.jpg`, { type: 'image/jpeg' });
    setSelectedFileAt(cameraCaptureCycle, capturedFile);
    resetAttachmentInputs(cameraCaptureCycle);
    closeCameraCapture();
  };

  const handleFileUpload = async (cycle: number) => {
    if (!plantTourId) {
      alert('Plant Tour ID is required for file upload');
      return;
    }
    const file = selectedFile[idx(cycle)];
    if (!file) {
      alert('Please select a file before uploading');
      return;
    }
    setIsUploadingAt(cycle, true);
    const productName = (formDataByCycle[idx(cycle)].productName || '').trim();
    if (!productName) {
      alert('Please enter a product name before uploading attachments');
      setIsUploadingAt(cycle, false);
      return;
    }
    const cycleNumber = Number(cycle);
    if (isNaN(cycleNumber) || cycleNumber < 1) {
      setIsUploadingAt(cycle, false);
      return;
    }
    try {
      if (isOfflineStarted) {
        await storeFileOffline(cycleNumber, file, plantTourId, productName);
        const offlineFiles = getFileDisplayData(cycleNumber, productName);
        setUploadedFilesAt(cycle, prev => {
          const existingIds = new Set(prev.map(f => f.id));
          const newFiles = offlineFiles.filter(f => !existingIds.has(f.id));
          return [...prev, ...newFiles];
        });
        alert(`File "${file.name}" stored offline for Cycle ${cycleNumber}, Product ${productName}. Will upload when online.`);
        setSelectedFileAt(cycle, null);
        resetAttachmentInputs(cycle);
        setIsUploadingAt(cycle, false);
        return;
      }
      const response = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      const accessToken = response.accessToken;
      if (!accessToken) throw new Error('Failed to get SharePoint access token');
      
      console.log('Uploading file with metadata:', {
        cycleNumber,
        plantTourId,
        productName,
        fileName: file.name
      });
      
      const result = await uploadFileToSharePointWithFile(cycleNumber, plantTourId, accessToken, file, productName);
      if (result.success) {
        const itemId = result.data?.itemId?.toString?.() ?? String(Date.now());
        const savedFileName = result.data?.fileName || file.name;
        setUploadedFilesAt(cycle, prev => [...prev, {
          id: itemId,
          cycleNum: cycle,
          productName,
          fileUrl: '',
          fileName: savedFileName,
          fileSize: file.size,
          uploadTime: Date.now(),
          isOffline: false,
        }]);
        alert(result.message);
        setSelectedFileAt(cycle, null);
        resetAttachmentInputs(cycle);
        await loadUploadedFiles();
      } else {
        alert(result.message);
      }
    } catch (error) {
      console.error('Upload error:', error);
      if (isOfflineStarted || (error instanceof Error && (error.message.includes('token') || error.message.includes('network')))) {
        try {
          await storeFileOffline(cycleNumber, file, plantTourId, productName);
          const offlineFiles = getFileDisplayData(cycleNumber, productName);
          setUploadedFilesAt(cycle, prev => {
            const existingIds = new Set(prev.map(f => f.id));
            const newFiles = offlineFiles.filter(f => !existingIds.has(f.id));
            return [...prev, ...newFiles];
          });
          alert(`File "${file.name}" stored offline. Will upload when online.`);
          setSelectedFileAt(cycle, null);
          resetAttachmentInputs(cycle);
        } catch (_) {}
      } else {
        alert(error instanceof Error ? error.message : 'Failed to upload file.');
      }
    } finally {
      setIsUploadingAt(cycle, false);
    }
  };

  const handleCompleteCycle = async (cycle: number) => {
    const products = savedProducts[idx(cycle)];
    if (!products.length) {
      alert('Please save at least one product before completing the cycle.');
      return;
    }
    setCycleCompletedAt(cycle, true);
    setIsSessionStartedAt(cycle, false);
    if (cycle === currentCycle) setCurrentCycle(prev => prev + 1);
    if (plantTourId && accounts.length > 0 && !isOfflineStarted) await loadUploadedFiles();
  };

  const toggleCompletedCycleExpansion = (cycleNum: number) => {
    setExpandedCompletedCycles(prev => {
      const next = new Set(prev);
      if (next.has(cycleNum)) next.delete(cycleNum);
      else next.add(cycleNum);
      return next;
    });
  };

  const loadUploadedFiles = async () => {
    if (!plantTourId) return;
    try {
      const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
      for (let c = 1; c <= MAX_CYCLES; c++) {
        const files = getFileDisplayData(c);
        if (files.length > 0) offlineFilesByCycle[c] = files;
      }
      if (isOfflineStarted) {
        setUploadedFiles(prev => prev.map((p, i) => {
          const cycleNum = i + 1;
          const off = offlineFilesByCycle[cycleNum] || [];
          const existingIds = new Set(off.map(f => f.id));
          const optimistic = p.filter(f => !existingIds.has(f.id));
          return [...off, ...optimistic];
        }));
        return;
      }
      if (!accounts.length) return;
      const response = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      const accessToken = response.accessToken;
      if (!accessToken) return;
      const files = await fetchUploadedFiles(plantTourId, accessToken);
      setUploadedFiles(prev => prev.map((p, i) => {
        const cycleNum = i + 1;
        const fromServer = files.filter(f => f.cycleNum === cycleNum);
        const off = offlineFilesByCycle[cycleNum] || [];
        const serverIds = new Set(fromServer.map(f => f.id));
        const offIds = new Set(off.map(f => f.id));
        const optimistic = p.filter(f => !serverIds.has(f.id) && !offIds.has(f.id));
        return [...fromServer, ...off, ...optimistic];
      }));
    } catch (error) {
      console.error('Error loading uploaded files:', error);
    }
  };

  useEffect(() => {
    if (!plantTourId) return;
    if (isOfflineStarted) {
      const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
      for (let c = 1; c <= MAX_CYCLES; c++) {
        const files = getFileDisplayData(c);
        if (files.length > 0) offlineFilesByCycle[c] = files;
      }
      setUploadedFiles(prev => prev.map((p, i) => {
        const cycleNum = i + 1;
        const off = offlineFilesByCycle[cycleNum] || [];
        const existingIds = new Set(off.map(f => f.id));
        const optimistic = p.filter(f => !existingIds.has(f.id));
        return [...off, ...optimistic];
      }));
      return;
    }
    if (!accounts.length) return;
    loadUploadedFiles();
  }, [plantTourId, accounts.length, isOfflineStarted]);

  const [viewingFileLoading, setViewingFileLoading] = useState<string | null>(null);
  const [viewingFile, setViewingFile] = useState<{ file: FileDisplayData; blobUrl: string } | null>(null);

  const isImageFile = (fileName: string): boolean => {
    return ['.jpg', '.jpeg', '.png', '.gif', '.bmp', '.webp'].some(ext => fileName.toLowerCase().endsWith(ext));
  };

  const handleViewFile = async (file: FileDisplayData) => {
    if (file.isOffline && file.fileUrl) {
      window.open(file.fileUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (isOfflineStarted && !file.isOffline) {
      alert('Cannot view online files in offline mode.');
      return;
    }
    setViewingFileLoading(file.id);
    const spSiteUrl = 'https://bectors.sharepoint.com/sites/PTMS_PRD';
    const libraryTitle = 'CodingDocuments';
    try {
      const response = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      const accessToken = response.accessToken;
      if (!accessToken && file.fileUrl) {
        window.open(file.fileUrl, '_blank');
        return;
      }
      let blob: Blob;
      if (file.fileUrl) {
        const baseUrl = file.fileUrl.replace(/\?.*$/, '');
        const serverRelativeUrl = new URL(baseUrl).pathname;
        const fetchUrl = `${spSiteUrl}/_api/web/GetFileByServerRelativeUrl('${encodeURIComponent(serverRelativeUrl)}')/$value`;
        const res = await fetch(fetchUrl, { method: 'GET', headers: { Authorization: `Bearer ${accessToken}`, Accept: '*/*' } });
        if (!res.ok) {
          const directRes = await fetch(baseUrl, { method: 'GET', headers: { Authorization: `Bearer ${accessToken}`, Accept: '*/*' } });
          blob = await directRes.blob();
        } else {
          blob = await res.blob();
        }
      } else if (file.id) {
        const byIdUrl = `${spSiteUrl}/_api/web/lists/getbytitle('${libraryTitle}')/items(${file.id})/File/$value`;
        const res = await fetch(byIdUrl, { method: 'GET', headers: { Authorization: `Bearer ${accessToken}`, Accept: '*/*' } });
        if (!res.ok) return;
        blob = await res.blob();
      } else return;
      if (!blob) return;
      setViewingFile({ file, blobUrl: URL.createObjectURL(blob) });
    } catch (error) {
      console.error('Error viewing file:', error);
      if (file.fileUrl) window.open(file.fileUrl, '_blank');
    } finally {
      setViewingFileLoading(null);
    }
  };

  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const handleDeleteFile = async (file: FileDisplayData) => {
    if (!window.confirm(`Delete "${file.fileName}"?`)) return;
    const removeFileFromState = () => {
      setUploadedFiles(prev =>
        prev.map(cycleFiles => cycleFiles.filter(f => f.id !== file.id))
      );
    };

    if (file.isOffline) {
      removeFileFromState();
      alert('File removed from this view.');
      return;
    }

    if (isOfflineStarted) {
      alert('Cannot delete files in offline mode.');
      return;
    }
    setDeletingFileId(file.id);
    try {
      const response = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
      const accessToken = response.accessToken;
      if (!accessToken) return;
      const result = await deleteFileFromSharePoint(file.id, accessToken);
      if (result.success) {
        removeFileFromState();
        alert('File deleted successfully');
        // Refresh in background to keep local state and server in sync.
        loadUploadedFiles();
      } else alert(result.message);
    } catch (error) {
      alert(error instanceof Error ? error.message : 'Failed to delete file.');
    } finally {
      setDeletingFileId(null);
    }
  };

  const loadCycleData = async () => {
    if (!plantTourId || isOfflineStarted) return;
    try {
      const cycles = await fetchCycleData(plantTourId);
      const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cycles.forEach((cycleData: CycleData) => {
        const cycleNum = parseInt(cycleData.cycleNum, 10);
        const i = idx(cycleNum);
        if (i < 0 || i >= MAX_CYCLES) return;
        byCycle[i].push({
          cycleNum: cycleData.cycleNum,
          product: cycleData.product !== 'N/A' ? cycleData.product : '',
          executiveName: cycleData.executiveName !== 'N/A' ? cycleData.executiveName : '',
          sku: cycleData.sku || '',
          machineProof: cycleData.machineProof || '',
          remarks: cycleData.remarks || '',
        });
      });
      setSavedProducts(byCycle);
      setCycleCompleted(prev => prev.map((v, i) => (byCycle[i].length > 0 ? true : v)));
      const maxCycleFromData = cycles.length ? Math.max(...cycles.map(cd => parseInt(String(cd.cycleNum), 10) || 0), 1) : 0;
      setCurrentCycle(maxCycleFromData + 1);
    } catch (error) {
      console.error('Error loading cycle data:', error);
    }
  };

  useEffect(() => {
    const anyStarted = isSessionStarted.some(Boolean);
    const anyCompleted = cycleCompleted.some(Boolean);
    if ((anyStarted || anyCompleted) && plantTourId && accounts.length > 0 && !isOfflineStarted) loadUploadedFiles();
  }, [isSessionStarted, cycleCompleted, plantTourId, isOfflineStarted]);

  useLayoutEffect(() => {
    if (!plantTourId || !isOfflineStarted) return;
    const fetched = store.getState()?.backingCoding?.fetchedCycles ?? [];
    if (!Array.isArray(fetched) || fetched.length === 0) return;
    const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
    fetched.forEach((cycleData: any) => {
      let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
      const cycleNum = parseInt(cycleNumStr, 10);
      if (isNaN(cycleNum) || cycleNum < 1) return;
      const i = idx(cycleNum);
      if (i >= 0 && i < MAX_CYCLES) {
        byCycle[i].push({
          cycleNum: String(cycleNum),
          product: cycleData.product || '',
          executiveName: cycleData.executiveName || '',
          sku: cycleData.sku || '',
          machineProof: cycleData.machineProof || '',
          remarks: cycleData.remarks || '',
        });
      }
    });
    const completedCycles = Array(MAX_CYCLES).fill(false);
    byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
    const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
    setSavedProducts(byCycle);
    setCycleCompleted(completedCycles);
    setCurrentCycle(maxCycleFromData + 1);
    const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
    for (let c = 1; c <= MAX_CYCLES; c++) {
      const files = getFileDisplayData(c);
      if (files.length > 0) offlineFilesByCycle[c] = files;
    }
    setUploadedFiles(prev => prev.map((p, i) => {
      const cycleNum = i + 1;
      const off = offlineFilesByCycle[cycleNum] || [];
      const existingIds = new Set(off.map(f => f.id));
      const optimistic = p.filter(f => !existingIds.has(f.id));
      return [...off, ...optimistic];
    }));
  }, [plantTourId, isOfflineStarted]);

  useEffect(() => {
    if (!plantTourId) return;
    if (isOfflineStarted) {
      const cyclesToUse = store.getState()?.backingCoding?.fetchedCycles ?? [];
      if (!Array.isArray(cyclesToUse) || cyclesToUse.length === 0) {
        setSavedProducts(Array(MAX_CYCLES).fill(null).map(() => []));
        setCycleCompleted(Array(MAX_CYCLES).fill(false));
        setCurrentCycle(1);
        setUploadedFiles(Array.from({ length: MAX_CYCLES }, () => []));
        return;
      }
      const byCycle: CycleProductData[][] = Array(MAX_CYCLES).fill(null).map(() => []);
      cyclesToUse.forEach((cycleData: any) => {
        let cycleNumStr = String(cycleData.cycleNum || '1').replace('Cycle-', '').trim();
        const cycleNum = parseInt(cycleNumStr, 10);
        if (isNaN(cycleNum) || cycleNum < 1) return;
        const i = idx(cycleNum);
        if (i >= 0 && i < MAX_CYCLES) {
          byCycle[i].push({
            cycleNum: String(cycleNum),
            product: cycleData.product || '',
            executiveName: cycleData.executiveName || '',
            sku: cycleData.sku || '',
            machineProof: cycleData.machineProof || '',
            remarks: cycleData.remarks || '',
          });
        }
      });
      setSavedProducts(byCycle);
      const completedCycles = Array(MAX_CYCLES).fill(false);
      byCycle.forEach((products, index) => { if (products.length > 0) completedCycles[index] = true; });
      const maxCycleFromData = byCycle.reduce((max, products, index) => (products.length > 0 ? Math.max(max, index + 1) : max), 0);
      setCurrentCycle(maxCycleFromData + 1);
      const offlineFilesByCycle: { [key: number]: FileDisplayData[] } = {};
      for (let c = 1; c <= MAX_CYCLES; c++) {
        const files = getFileDisplayData(c);
        if (files.length > 0) offlineFilesByCycle[c] = files;
      }
      setUploadedFiles(prev => prev.map((p, i) => {
        const cycleNum = i + 1;
        const off = offlineFilesByCycle[cycleNum] || [];
        const existingIds = new Set(off.map(f => f.id));
        const optimistic = p.filter(f => !existingIds.has(f.id));
        return [...off, ...optimistic];
      }));
      return;
    }
    loadCycleData();
  }, [plantTourId, isOfflineStarted, reduxFetchedCycles?.length]);

  useEffect(() => {
    if (cameraCaptureCycle === null || !cameraStreamRef.current || !cameraVideoRef.current) return;
    const video = cameraVideoRef.current;
    video.srcObject = cameraStreamRef.current;
    const playVideo = async () => {
      try {
        await video.play();
        setIsCameraReady(true);
      } catch (error) {
        console.error('Failed to start camera preview:', error);
        setCameraError('Unable to start camera preview.');
        setIsCameraReady(false);
      }
    };
    playVideo();
  }, [cameraCaptureCycle]);

  useEffect(() => () => stopCameraStream(), []);

  useEffect(() => {
    const initial = getBackingCodingInitial();
    if (!initial?.productName && !initial?.executiveName) return;
    setFormDataByCycle(prev => prev.map((p, i) => {
      const cycleNum = i + 1;
      if (cycleNum === 1) {
        if (p.productName || p.executiveName) return p;
        return { productName: initial?.productName ?? '', executiveName: initial?.executiveName ?? '' };
      }
      if (!cycleCompleted[cycleNum - 2]) return p;
      if (p.productName || p.executiveName) return p;
      return { productName: initial?.productName ?? '', executiveName: initial?.executiveName ?? '' };
    }));
  }, [cycleCompleted]);

  const getFilesForProduct = (cycleNum: number, productData: CycleProductData) => {
    // Filter files by cycle number first
    const cycleFiles = (uploadedFiles[idx(cycleNum)] || []).filter(
      (f) => (Number(f.cycleNum) || 1) === cycleNum
    );
    if (cycleFiles.length === 0) return [];

    const productKey = (productData.product || '').trim().toLowerCase();
    
    // STRICT MATCHING: Only show files that match BOTH cycle number AND product name
    // This ensures each product only sees its own attachments
    if (productKey) {
      const matchedFiles = cycleFiles.filter((f) => {
        const fileProductName = (f.productName || '').trim().toLowerCase();
        return fileProductName === productKey && fileProductName !== '';
      });
      
      // If we found files with matching product name, return them
      if (matchedFiles.length > 0) {
        return matchedFiles;
      }
      
      // If product name exists but no files match, return empty (don't show other product's files)
      return [];
    }

    // Only if product name is empty, fall back to distributing unscoped files
    // This handles edge cases where product names might be missing
    const unscopedFiles = cycleFiles.filter((f) => !(f.productName || '').trim());
    const totalProducts = savedProducts[idx(cycleNum)].length;

    if (unscopedFiles.length > 0 && totalProducts > 0) {
      // Distribute unscoped files evenly across products by upload order
      const productIndex = savedProducts[idx(cycleNum)].findIndex((p) => p === productData);
      if (productIndex >= 0) {
        const sortedUnscoped = unscopedFiles.slice().sort((a, b) => a.uploadTime - b.uploadTime);
        const filesPerProduct = Math.floor(sortedUnscoped.length / totalProducts);
        const remainder = sortedUnscoped.length % totalProducts;
        const start = productIndex * filesPerProduct + Math.min(productIndex, remainder);
        const end = start + filesPerProduct + (productIndex < remainder ? 1 : 0);
        return sortedUnscoped.slice(start, end);
      }
    }

    return [];
  };

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="w-full mb-6">
          <div className="px-4 md:px-6 py-3">
            <button onClick={handleBack} className="flex items-center text-gray-800 hover:text-gray-900 transition-colors">
              <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <span className="font-medium">Back</span>
            </button>
          </div>
          <div className="bg-gray-50 px-4 md:px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-gray-800">Backing Coding</h1>
                <div className="flex items-center gap-1.5 bg-gray-200 border border-gray-300 rounded-full px-3 py-1">
                  <div className="w-1.5 h-1.5 bg-gray-600 rounded-full" />
                  <span className="text-sm text-gray-700">{selectedCycle || 'Shift 1'}</span>
                </div>
                <span className="text-gray-600">•</span>
                <span className="text-sm text-gray-700">{formattedDate}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="w-full px-4 md:px-6 space-y-6">
          {/* Completed cycles */}
          {Array.from({ length: MAX_CYCLES }, (_, i) => i + 1)
            .filter(n => cycleCompleted[idx(n)] || savedProducts[idx(n)].length > 0)
            .map(n => (
              <div key={n} className="bg-white rounded-lg shadow-md border border-gray-200 overflow-hidden">
                <div
                  className="flex items-center justify-between p-4 cursor-pointer border-b border-gray-200"
                  onClick={() => toggleCompletedCycleExpansion(n)}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-800">Cycle {n}</h2>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 transition-transform ${expandedCompletedCycles.has(n) ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {expandedCompletedCycles.has(n) && (
                  <div className="p-4 space-y-6">
                    {savedProducts[idx(n)].map((productData, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Product</div>
                              <div className="text-sm font-semibold text-gray-800">{productData.product || 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                              <div className="text-sm font-semibold text-gray-800">{productData.executiveName || 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                        <h3 className="text-sm font-semibold text-red-600 mb-3">Summary</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-gray-200">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">SKU</th>
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Machine Proof</th>
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Remarks</th>
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Attachments</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.sku || '-'}</td>
                                <td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.machineProof || '-'}</td>
                                <td className="border border-gray-200 px-3 py-2 text-gray-900">{productData.remarks || '-'}</td>
                                <td className="border border-gray-200 px-3 py-2">
                                  {getFilesForProduct(n, productData).length > 0 ? (
                                    <div className="space-y-2">
                                      {getFilesForProduct(n, productData).map(file => (
                                        <div key={file.id} className="flex items-center gap-2">
                                          {isImageFile(file.fileName) && file.fileUrl ? (
                                            <img src={file.fileUrl} alt="" className="w-12 h-12 object-cover rounded border border-gray-200" />
                                          ) : (
                                            <div className="w-12 h-12 bg-gray-100 rounded border border-gray-200 flex items-center justify-center">
                                              <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                              </svg>
                                            </div>
                                          )}
                                          <div className="flex-1 min-w-0">
                                            <div className="text-xs font-medium text-gray-900 truncate">{file.fileName}</div>
                                            <div className="text-xs text-gray-500">
                                              Modified: {new Date(file.uploadTime).toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })} {new Date(file.uploadTime).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' })}
                                            </div>
                                          </div>
                                          <button
                                            type="button"
                                            onClick={() => handleViewFile(file)}
                                            disabled={viewingFileLoading === file.id}
                                            className="text-xs px-2 py-1 rounded border border-gray-300 text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                                          >
                                            {viewingFileLoading === file.id ? 'Opening…' : 'View'}
                                          </button>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <span className="text-gray-500 text-xs">No attachments</span>
                                  )}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

          {/* Current cycle card */}
          <div className="bg-white rounded-lg shadow-md border border-gray-200 p-6">
            {!cycleCompleted[idx(currentCycle)] && (
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-gray-800">Cycle {currentCycle}</h2>
              </div>
            )}

            {/* Start section: Product Name, Executive Name, Start Session */}
            {!isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (savedProducts[idx(currentCycle)].length === 0 || showStartFormForNewProduct[idx(currentCycle)]) && (
              <div className="mb-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Product Name</label>
                    <input
                      type="text"
                      name="productName"
                      value={formDataByCycle[idx(currentCycle)].productName}
                      onChange={e => handleInputChange(e, currentCycle)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter product name"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Executive Name</label>
                    <input
                      type="text"
                      name="executiveName"
                      value={formDataByCycle[idx(currentCycle)].executiveName}
                      onChange={e => handleInputChange(e, currentCycle)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter executive name"
                    />
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    onClick={() => handleStartSession(currentCycle)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Start Session
                  </button>
                </div>
              </div>
            )}

            {/* Completed current cycle - show summary (same as completed list) */}
            {cycleCompleted[idx(currentCycle)] && (
              <>
                <div
                  className="flex items-center justify-between mb-4 cursor-pointer"
                  onClick={() => setCycleExpandedAt(currentCycle, !cycleExpanded[idx(currentCycle)])}
                >
                  <div className="flex items-center gap-3">
                    <h2 className="text-lg font-bold text-gray-800">Cycle {currentCycle}</h2>
                    <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center">
                      <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                    </div>
                  </div>
                  <svg className={`w-5 h-5 text-gray-500 ${cycleExpanded[idx(currentCycle)] ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </div>
                {cycleExpanded[idx(currentCycle)] && (
                  <div className="space-y-6">
                    {savedProducts[idx(currentCycle)].map((productData, index) => (
                      <div key={index} className="border-b border-gray-200 pb-6 last:border-b-0">
                        <div className="bg-blue-50 rounded-lg p-4 mb-4">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Product</div>
                              <div className="text-sm font-semibold text-gray-800">{productData.product || 'N/A'}</div>
                            </div>
                            <div>
                              <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                              <div className="text-sm font-semibold text-gray-800">{productData.executiveName || 'N/A'}</div>
                            </div>
                          </div>
                        </div>
                        <h3 className="text-sm font-semibold text-red-600 mb-3">Summary</h3>
                        <div className="overflow-x-auto">
                          <table className="w-full text-sm border border-gray-200">
                            <thead>
                              <tr className="bg-gray-50">
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">SKU</th>
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Machine Proof</th>
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Remarks</th>
                                <th className="border border-gray-200 px-3 py-2 text-left font-medium text-gray-700">Attachments</th>
                              </tr>
                            </thead>
                            <tbody>
                              <tr>
                                <td className="border border-gray-200 px-3 py-2">{productData.sku || '-'}</td>
                                <td className="border border-gray-200 px-3 py-2">{productData.machineProof || '-'}</td>
                                <td className="border border-gray-200 px-3 py-2">{productData.remarks || '-'}</td>
                                <td className="border border-gray-200 px-3 py-2">
                                  {getFilesForProduct(currentCycle, productData).map(file => (
                                    <div key={file.id} className="flex items-center gap-2 mb-1">
                                      <span className="text-xs truncate">{file.fileName}</span>
                                      <button type="button" onClick={() => handleViewFile(file)} disabled={viewingFileLoading === file.id} className="text-xs text-blue-600 hover:underline">
                                        {viewingFileLoading === file.id ? 'Opening…' : 'View'}
                                      </button>
                                    </div>
                                  ))}
                                  {getFilesForProduct(currentCycle, productData).length === 0 && <span className="text-gray-500 text-xs">No attachments</span>}
                                </td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}

            {/* After save: show "Add new product" and "Complete Cycle" */}
            {savedProducts[idx(currentCycle)].length > 0 && !cycleCompleted[idx(currentCycle)] && !isSessionStarted[idx(currentCycle)] && !showStartFormForNewProduct[idx(currentCycle)] && (
              <div className="mb-6">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <p className="text-green-700 font-semibold mb-3">
                    {savedProducts[idx(currentCycle)].length} product(s) saved in Cycle {currentCycle}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setShowStartFormForNewProductAt(currentCycle, true);
                        setFormDataByCycleAt(currentCycle, {
                          productName: getBackingCodingInitial()?.productName ?? '',
                          executiveName: getBackingCodingInitial()?.executiveName ?? '',
                        });
                        setSaveFormDataByCycleAt(currentCycle, { sku: '', machineProof: '', remarks: '' });
                        setUploadedFilesAt(currentCycle, () => []);
                        setSelectedFileAt(currentCycle, null);
                      }}
                      className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium"
                    >
                      Add new product
                    </button>
                    <button
                      type="button"
                      onClick={() => handleCompleteCycle(currentCycle)}
                      className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-md text-sm font-medium"
                    >
                      Complete Cycle & Move to Next
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Save section: Product/Executive card, SKU, Machine Proof, Remarks, Upload, Cancel, Save Session */}
            {isSessionStarted[idx(currentCycle)] && !cycleCompleted[idx(currentCycle)] && (
              <>
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Product</div>
                      <div className="text-sm font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].productName || 'N/A'}</div>
                    </div>
                    <div>
                      <div className="text-xs text-gray-600 mb-1">Executive Name</div>
                      <div className="text-sm font-semibold text-gray-800">{formDataByCycle[idx(currentCycle)].executiveName || 'N/A'}</div>
                    </div>
                  </div>
                </div>
                <div className="space-y-4 mb-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">SKU</label>
                    <input
                      type="text"
                      name="sku"
                      value={saveFormDataByCycle[idx(currentCycle)].sku}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Machine Proof</label>
                    <input
                      type="text"
                      name="machineProof"
                      value={saveFormDataByCycle[idx(currentCycle)].machineProof}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Enter value"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Major Defects and Remarks</label>
                    <textarea
                      name="remarks"
                      value={saveFormDataByCycle[idx(currentCycle)].remarks}
                      onChange={e => handleInputChange(e, currentCycle, true)}
                      rows={4}
                      className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                      placeholder="Type Here..."
                    />
                  </div>
                </div>
                <div className="mb-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Upload Attachment</label>
                  <div className="border border-gray-300 rounded-md p-4">
                    <div className="flex items-center gap-4 mb-2">
                      <button
                        type="button"
                        onClick={() => triggerSystemImagePicker(currentCycle)}
                        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-200"
                      >
                        Choose from system
                      </button>
                      <button
                        type="button"
                        onClick={() => openCameraCapture(currentCycle)}
                        className="px-4 py-2 bg-gray-100 border border-gray-300 rounded-md text-sm text-gray-700 hover:bg-gray-200"
                      >
                        Take photo
                      </button>
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={el => { systemFileInputRefs.current[idx(currentCycle)] = el; }}
                        onChange={e => handleFileSelect(e, currentCycle)}
                      />
                      <input
                        type="file"
                        accept="image/*"
                        capture="environment"
                        className="hidden"
                        ref={el => { cameraFileInputRefs.current[idx(currentCycle)] = el; }}
                        onChange={e => handleFileSelect(e, currentCycle)}
                      />
                      <span className="text-sm text-gray-500">{selectedFile[idx(currentCycle)]?.name ?? 'No file chosen'}</span>
                    </div>
                    <p className="text-xs text-gray-500 mb-3">You can upload supporting documents or images here.</p>
                    <button
                      onClick={() => handleFileUpload(currentCycle)}
                      disabled={!selectedFile[idx(currentCycle)] || isUploading[idx(currentCycle)]}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 text-sm font-medium disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {isUploading[idx(currentCycle)] ? 'Uploading...' : 'Upload'}
                    </button>
                    <div className="mt-4">
                      {(() => {
                        // Filter files by cycle number AND current product name
                        const currentProductName = (formDataByCycle[idx(currentCycle)].productName || '').trim().toLowerCase();
                        const filesForCurrentProduct = (uploadedFiles[idx(currentCycle)] || []).filter(f => {
                          const matchesCycle = (Number(f.cycleNum) || 1) === currentCycle;
                          if (!matchesCycle) return false;
                          // Match by product name (case-insensitive)
                          const fileProductName = (f.productName || '').trim().toLowerCase();
                          return fileProductName === currentProductName && currentProductName !== '';
                        });
                        
                        return filesForCurrentProduct.length > 0 ? (
                          <div className="space-y-2">
                            {filesForCurrentProduct.map(file => (
                              <div key={file.id} className="flex items-center justify-between bg-gray-50 p-2 rounded border border-gray-200">
                                <span className="text-xs text-gray-600 truncate">{file.fileName}</span>
                                <div className="flex items-center gap-2">
                                  <button type="button" onClick={() => handleViewFile(file)} disabled={viewingFileLoading === file.id} className="text-xs text-blue-600 hover:underline">
                                    {viewingFileLoading === file.id ? 'Opening…' : 'View'}
                                  </button>
                                  {!isOfflineStarted && (
                                    <button type="button" onClick={() => handleDeleteFile(file)} disabled={deletingFileId === file.id} className="text-xs text-red-600 hover:underline">
                                      {deletingFileId === file.id ? 'Deleting…' : 'Delete'}
                                    </button>
                                  )}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-gray-500">No files uploaded yet for this product. Choose a file above and click Upload.</p>
                        );
                      })()}
                    </div>
                  </div>
                </div>
                <div className="flex justify-end gap-4">
                  <button
                    onClick={() => handleCancelSession(currentCycle)}
                    className="px-6 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 font-medium"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={() => handleSaveSession(currentCycle)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 font-medium"
                  >
                    Save Session
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {viewingFile && (
          <div
            className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4"
            onClick={() => {
              if (viewingFile.blobUrl) URL.revokeObjectURL(viewingFile.blobUrl);
              setViewingFile(null);
            }}
          >
            <div className="bg-white rounded-lg max-w-4xl max-h-[90vh] w-full overflow-auto" onClick={e => e.stopPropagation()}>
              <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800 truncate flex-1 mr-4">{viewingFile.file.fileName}</h3>
                <button
                  onClick={() => {
                    if (viewingFile.blobUrl) URL.revokeObjectURL(viewingFile.blobUrl);
                    setViewingFile(null);
                  }}
                  className="text-gray-500 hover:text-gray-700 text-2xl font-bold"
                >
                  ×
                </button>
              </div>
              <div className="p-4">
                {isImageFile(viewingFile.file.fileName) ? (
                  <img src={viewingFile.blobUrl} alt={viewingFile.file.fileName} className="max-w-full h-auto mx-auto rounded-lg" />
                ) : (
                  <div className="text-center py-8">
                    <p className="text-gray-600 mb-4">Preview not available for this file type.</p>
                    <a href={viewingFile.blobUrl} download={viewingFile.file.fileName} className="inline-block px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700">
                      Download File
                    </a>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
        {cameraCaptureCycle !== null && (
          <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-800">Take photo</h3>
                <button type="button" onClick={closeCameraCapture} className="text-gray-500 hover:text-gray-700 text-2xl font-bold">×</button>
              </div>
              <div className="p-4 space-y-3">
                {cameraError ? (
                  <p className="text-sm text-red-600">{cameraError}</p>
                ) : (
                  <video
                    ref={cameraVideoRef}
                    autoPlay
                    playsInline
                    muted
                    onLoadedMetadata={() => setIsCameraReady(true)}
                    onCanPlay={() => setIsCameraReady(true)}
                    className="w-full max-h-[60vh] bg-black rounded"
                  />
                )}
                <canvas ref={cameraCanvasRef} className="hidden" />
                <div className="flex justify-end gap-3">
                  <button type="button" onClick={closeCameraCapture} className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50">
                    Cancel
                  </button>
                  {!cameraError && (
                    <button
                      type="button"
                      onClick={handleCapturePhoto}
                      disabled={!isCameraReady}
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Capture
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default BackingCoding;
